/**
 * Product card behaviour.
 *
 * Four jobs, all progressive enhancements over markup that already works without them:
 * the hover image, the quick-add control, the scroll affordance on the specification
 * band, and the grid's entry cascade. Every one of them degrades to something usable if
 * this file never loads.
 */

(function () {
  'use strict';

  /* Several sections render card grids and each loads this file, so the tag can appear
     more than once on a page. Without this the section-load listener would be bound
     repeatedly. */
  window.Hubcart = window.Hubcart || {};
  if (window.Hubcart.productCard) return;

  /* The same test the stylesheet makes. A coarse pointer has no hover to reveal anything
     with, so the second image is not merely hidden there — it is never fetched. */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  /**
   * Media carousel.
   *
   * The reference's card does not zoom or cross-fade on hover: its `secondary-media`
   * element (theme.js) lays every product image into a Flickity carousel over the
   * primary and maps the pointer's position across the frame onto a slide — with two
   * images any hover shows the second; with three the frame splits into halves; with
   * four into thirds; five or more get no pointer mapping at all, only the dots.
   * Leaving the card always slides back to the first image. This is that machine
   * rebuilt without Flickity: one translated track under a clipping viewport, moved on
   * the same duration and curve the reference animates with (`--animation-primary`,
   * which is our `--duration-base` + `--ease-primary`).
   *
   * All the images live in a <template>, whose contents a browser never fetches. On a
   * touch device there is no gesture that could drive any of this — `responsive-spec.md`
   * §7.2 item 2 says such media must be reachable or absent — so the carousel never
   * hydrates there and a phone downloads one image per card. Where it can hydrate, it
   * waits until the card is near the viewport, the same 200px margin the reference
   * gives its own lazy `inView` initialiser.
   */
  const nearViewport =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              entry.target.hydrate();
            });
          },
          { rootMargin: '200px 0px 200px 0px' }
        )
      : null;

  if (!customElements.get('card-carousel')) {
    customElements.define(
      'card-carousel',
      class CardCarousel extends HTMLElement {
        connectedCallback() {
          if (this.hydrated || !canHover.matches) return;
          if (nearViewport) {
            nearViewport.observe(this);
          } else {
            this.hydrate();
          }
        }

        disconnectedCallback() {
          if (nearViewport) nearViewport.unobserve(this);
        }

        hydrate() {
          if (this.hydrated) return;
          this.hydrated = true;

          const template = this.parentElement.querySelector('template[data-card-carousel]');
          if (!template) return;

          /* The same viewport/track split the reference gets from Flickity: the track
             carries the cells and translates; the viewport clips it. The dots must NOT
             be clipped — they hang below the frame — which is exactly why the clip
             cannot live on this element or any ancestor of both. */
          this.track = document.createElement('div');
          this.track.className = 'card__carousel-track';
          this.track.append(template.content);
          template.remove();

          const viewport = document.createElement('div');
          viewport.className = 'card__carousel-viewport';
          viewport.append(this.track);
          this.append(viewport);

          this.count = this.track.children.length;
          this.index = 0;

          /* The reference renders dots for every carousel but hides them at two images
             (`without-dots`) — a lone dot for "the other image" says nothing the hover
             doesn't. It also hides the first dot at any count, so the row reads as "the
             other views", not as a position gauge. Building only what will show keeps
             the DOM honest; `tabindex="-1"` because the element is aria-hidden and the
             pointer is the only thing that can reach these anyway. */
          if (this.count >= 3) {
            this.dots = document.createElement('div');
            this.dots.className = 'card__carousel-dots';
            for (let i = 0; i < this.count; i += 1) {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'card__carousel-dot';
              dot.tabIndex = -1;
              /* A dot lives inside the media link; without these a click would ride
                 the bubble up to the anchor and open the product instead of sliding. */
              dot.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.select(i);
              });
              this.dots.append(dot);
            }
            this.append(this.dots);
          }

          /* The reference's pointer map, verbatim: nothing at five or more. */
          if (this.count <= 4) {
            this.addEventListener('mousemove', (event) => {
              const { left, width } = this.getBoundingClientRect();
              const x = event.clientX - left;
              if (this.count === 2) {
                this.select(1);
              } else if (this.count === 3) {
                this.select(x < width / 2 ? 1 : 2);
              } else {
                this.select(x < width / 3 ? 1 : x < (width / 3) * 2 ? 2 : 3);
              }
            });
          }
          this.addEventListener('mouseleave', () => this.select(0));

          /* Only once the carousel is genuinely standing in does the fallback image
             yield; before this line the empty element is transparent and the card is
             the plain one every other pointer sees. */
          this.closest('.card__media-link').classList.add('card__media-link--live');
          this.closest('.card__media').classList.add('card__media--carousel');
          this.select(0);
        }

        select(index) {
          if (index === this.index && this.track.style.transform) return;
          this.index = index;
          this.track.style.transform = 'translateX(' + index * -100 + '%)';
          if (this.dots) {
            Array.prototype.forEach.call(this.dots.children, (dot, i) => {
              dot.classList.toggle('is-selected', i === index);
            });
          }
        }
      }
    );
  }

  /**
   * Quick add.
   *
   * Wraps a real <form> posting to /cart/add, so the control still adds to the cart with
   * JavaScript disabled — it just lands on the cart page instead of staying put. Only
   * single-variant products get one; anything with options has to be chosen on its own
   * page, and `snippets/product-card.liquid` renders a link instead.
   */
  if (!customElements.get('quick-add')) {
    customElements.define(
      'quick-add',
      class QuickAdd extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.button = this.querySelector('button[type="submit"]');
          /* Only the label changes between states. Writing to the button itself would
             delete the icon beside it, and on a phone the icon is the whole control. */
          this.label = this.querySelector('[data-quick-add-label]');
          if (!this.form || !this.button) return;

          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        disconnectedCallback() {
          clearTimeout(this.resetTimer);
        }

        async onSubmit(event) {
          event.preventDefault();
          if (this.button.hasAttribute('aria-disabled')) return;

          this.setState('adding', this.dataset.labelAdding);

          try {
            /* The binding contract between this card and the cart: whatever added the
               line announces it on `document`, and every cart surface — drawer, header
               count, cart page — re-reads from that one event rather than from here.
               When the shared layer did the adding it has already made that
               announcement, carrying the re-rendered sections the drawer needs; a
               second event from here would make every cart surface fetch again for
               markup it already has. Only the fallback below has to announce itself. */
            if (window.Hubcart.cart && typeof window.Hubcart.cart.add === 'function') {
              await window.Hubcart.cart.add(this.dataset.variantId, 1);
            } else {
              const cart = await this.postDirect();
              document.dispatchEvent(
                new CustomEvent('hubcart:cart:updated', {
                  bubbles: true,
                  detail: { cart },
                })
              );
            }
            this.setState('added', this.dataset.labelAdded);
          } catch (error) {
            /* The API's own reason — usually an inventory ceiling — is more useful than
               a generic failure, and is the only place the shopper will see it. */
            this.setState('error', error.message || this.dataset.labelError);
          }

          clearTimeout(this.resetTimer);
          this.resetTimer = setTimeout(() => this.setState('idle', this.dataset.labelIdle), 2600);
        }

        /**
         * The fallback for a page that loaded this file but not `cart.js`. It fetches the
         * resulting cart itself so the event detail is the same shape the shared layer
         * publishes, and a card on such a page still updates the header count.
         */
        async postDirect() {
          const routes = window.Hubcart.routes || {};
          const response = await fetch(routes.cartAdd || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: this.dataset.variantId, quantity: 1 }] }),
          });

          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message);

          const cart = await fetch(routes.cart || '/cart.js').then((res) => res.json());
          return cart;
        }

        setState(state, label) {
          this.dataset.state = state;
          if (this.label) this.label.textContent = label || this.dataset.labelIdle;

          /* `aria-disabled` rather than `disabled`: a disabled button loses focus, which
             would drop a keyboard user out of the card mid-action. */
          if (state === 'adding') {
            this.button.setAttribute('aria-disabled', 'true');
          } else {
            this.button.removeAttribute('aria-disabled');
          }
        }
      }
    );
  }

  /**
   * Specification band.
   *
   * The band fades its trailing edge to say "there is more here". That fade is a lie once
   * the band is scrolled to its end, so it is removed there — which is the only thing this
   * element does. Without it the band still scrolls and still fades; the fade is simply
   * always on, which is the pre-existing behaviour and not a defect worth blocking paint.
   */
  if (!customElements.get('scroll-strip')) {
    customElements.define(
      'scroll-strip',
      class ScrollStrip extends HTMLElement {
        connectedCallback() {
          this.scroller = this.firstElementChild;
          if (!this.scroller) return;

          this.update = this.update.bind(this);
          this.scroller.addEventListener('scroll', this.update, { passive: true });

          /* The band's own width changes with the card, so a viewport resize can turn an
             overflowing band into one that fits. One observer per band is cheaper than a
             resize listener that has to re-measure every card on the page. */
          if ('ResizeObserver' in window) {
            this.observer = new ResizeObserver(this.update);
            this.observer.observe(this.scroller);
          }

          this.update();
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.scroller) this.scroller.removeEventListener('scroll', this.update);
        }

        update() {
          const el = this.scroller;
          /* A pixel of tolerance: fractional layout widths mean `scrollLeft` rarely
             reaches `scrollWidth - clientWidth` exactly. */
          const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
          this.toggleAttribute('data-scroll-end', atEnd);
        }
      }
    );
  }

  /**
   * Grid entry.
   *
   * The reference wraps every card grid — collection, search, the homepage's featured
   * collections — in a `motion-list` element (theme.js) that hides its cards the moment
   * it constructs and animates them in when the grid first enters the viewport: cards
   * already on screen rise 50px over 0.5s, each starting 100ms after the one before;
   * once that cascade finishes, every below-fold card runs the same rise together,
   * unstaggered, so scrolling on finds them already settled. On a small viewport or a
   * touch device the numbers shrink to 30px, 0.3s, 50ms. It runs once per element:
   * filtered results arrive with the animation suppressed (`component-facets.js` sets
   * `data-static` where the reference sets `motion-reduced`), and a paginated page load
   * is a fresh element.
   *
   * Two of the reference's choices are reproduced rather than corrected:
   *
   * - "On screen" is its own test, `rect.top < innerHeight + scrollY`, which mixes a
   *   viewport-relative top with the page scroll offset. At the top of the page that is
   *   an exact viewport test; scrolled down — the homepage's lower grids — it counts
   *   every card as visible, so those grids stagger all their cards. The scrolled
   *   behaviour is what the reference visibly does, so the arithmetic is kept, not
   *   fixed.
   * - The easing is the CSS `ease` keyword — Motion One's `animate` default, which the
   *   reference never overrides for lists.
   *
   * The hide is inline style written by JS, not a stylesheet rule, so markup without
   * JavaScript is simply visible, and a grid this element never touches (a `data-static`
   * facet swap) simply renders. Durations and the stagger scale with the theme's motion
   * settings; at zero — reduced motion, or the merchant toggle — the element does
   * nothing at all, which is the reference's own reduced-motion path.
   */
  if (!customElements.get('motion-grid')) {
    customElements.define(
      'motion-grid',
      class MotionGrid extends HTMLElement {
        connectedCallback() {
          if (this.entered || this.hasAttribute('data-static')) return;

          const root = getComputedStyle(document.documentElement);
          const motion =
            (parseFloat(root.getPropertyValue('--motion-scale')) || 0) *
            (parseFloat(root.getPropertyValue('--motion-factor')) || 0);
          if (!motion) return;

          /* The reference's `mqlSmall || isTouch` split: one parameter set for small
             screens and touch devices, another for pointer desktops. */
          const small =
            window.matchMedia('(max-width: 767px)').matches ||
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0;
          /* Desktop runs slower than the reference's 500/100: Patrick judged the grid's
             entry too quick on a laptop (merchant decision, 2026-09-07), so the rise and
             the step between cards are both stretched. Small screens keep the reference
             numbers — the cascade competes with scrolling there and longer delays read
             as the page lagging behind the thumb. */
          this.distance = small ? 30 : 50;
          this.duration = (small ? 300 : 800) * motion;
          this.stagger = (small ? 50 : 150) * motion;

          const cards = this.querySelectorAll('.card');
          if (!cards.length) return;

          cards.forEach((card) => {
            card.style.transform = 'translateY(' + this.distance + 'px)';
            card.style.opacity = '0';
            card.style.visibility = 'hidden';
          });

          this.observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.enter(cards);
          });
          this.observer.observe(this);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        async enter(cards) {
          this.entered = true;

          const limit = window.innerHeight + window.scrollY;
          const visible = [];
          const settled = [];
          cards.forEach((card) => {
            const rect = card.getBoundingClientRect();
            (rect.top < limit && rect.bottom > 0 ? visible : settled).push(card);
          });

          await Promise.all(visible.map((card, i) => this.rise(card, i * this.stagger)));
          settled.forEach((card) => this.rise(card, 0));
        }

        rise(card, delay) {
          /* Clearing the inline hide and animating from the hidden keyframe with a
             backwards fill happen in one task, so the card is never painted bare: the
             fill covers the stagger delay, and when the animation ends the card is
             left in its natural, style-free state. */
          card.style.transform = '';
          card.style.opacity = '';
          card.style.visibility = '';
          const animation = card.animate(
            {
              transform: ['translateY(' + this.distance + 'px)', 'translateY(0)'],
              opacity: [0, 1],
              visibility: ['hidden', 'visible'],
            },
            { duration: this.duration, delay: delay, easing: 'ease', fill: 'backwards' }
          );
          /* A card removed mid-flight — a facet swap landing during the cascade —
             rejects `finished`; the swap was the interruption, nothing to recover. */
          return animation.finished.catch(function () {});
        }
      }
    );
  }

  /* No load or section-render hook: all four behaviours are custom elements now, and
     custom elements upgrade themselves on insertion — including into markup the theme
     editor re-renders. */
  window.Hubcart.productCard = true;
})();
